import type { VFC } from 'react'
import { styles } from './styles'
import type { ImageProps } from './types'

export type { ImageProps }

interface AriaAnnotation {
  'aria-hidden'?: boolean
  'aria-label'?: string
  role?: string
}

/**
 * Render an image URL.
 *
 * @note This component only renders the `src` URL, and has no support for
 *   transforms. The image at the URL should *not* be the original, full-size
 *   image unless that's specifically what you need. In almost every case
 *   you'll want to render a scaled-down and/or cropped image. Any transforms
 *   must be applied to `src` before it's passed here, probably using the
 *   Cassini `<ImageProxy>`.
 */
export const Image: VFC<ImageProps> = ({
  alt = '',
  ariaLabel,
  ariaMode = 'visible',
  onClick,
  imageFit = 'contain',
  onError,
  onLoad,
  src,
  styleOverrides,
  title,
}) => {
  const ariaAnnotation: AriaAnnotation = {}
  if (ariaMode === 'hidden') {
    ariaAnnotation['aria-hidden'] = true
  }
  if (ariaLabel) {
    ariaAnnotation['aria-label'] = ariaLabel
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <img
      alt={alt}
      css={[styles.base(imageFit), styleOverrides]}
      onClick={onClick}
      onError={onError}
      onLoad={onLoad}
      src={src}
      title={title}
      {...ariaAnnotation}
    />
  )
}
Image.displayName = 'Lapidary: Image'
